/**
 * Tiny in-memory cache for API GET responses with stale-while-revalidate.
 *
 * Usage:
 *   import { cachedGet, primeCache } from '../lib/apiCache';
 *
 *   const data = await cachedGet('/api/products/123');   // hits network 1st time, cache afterwards
 *   primeCache('/api/products/123', product);            // pre-fill cache (e.g. on hover)
 *
 * The cache is module-scoped (lives for the lifetime of the SPA session) and
 * keyed by URL. Data is considered fresh for FRESH_TTL ms; after that it's
 * still returned synchronously but a background revalidation kicks in.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cache = new Map(); // key -> { data, ts, inflight }
const FRESH_TTL = 60 * 1000; // 60s "fresh" window; after, revalidate in background

function fullUrl(path) {
  if (!path) return path;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api')) return `${BACKEND_URL}${path}`;
  return `${API}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Return cached value synchronously if present (may be stale). */
export function getCached(path) {
  const entry = cache.get(fullUrl(path));
  return entry ? entry.data : undefined;
}

/** Pre-fill the cache (e.g. when you already have data from a list view). */
export function primeCache(path, data) {
  cache.set(fullUrl(path), { data, ts: Date.now() });
}

/** Invalidate one path or everything matching a prefix. */
export function invalidateCache(prefix = '') {
  const full = fullUrl(prefix);
  for (const key of Array.from(cache.keys())) {
    if (!prefix || key.startsWith(full)) cache.delete(key);
  }
}

/**
 * Stale-while-revalidate GET:
 *   - returns cached data immediately (resolves the promise synchronously
 *     via microtask) when present, even if stale
 *   - kicks off a network fetch when missing or older than FRESH_TTL
 *   - de-duplicates concurrent fetches for the same URL
 *
 * Callers can ignore the returned promise for the "fast path" – use
 * `getCached()` first for synchronous read, then `cachedGet()` to refresh.
 */
export async function cachedGet(path, options = {}) {
  const url = fullUrl(path);
  const now = Date.now();
  const entry = cache.get(url);

  if (entry) {
    const fresh = now - entry.ts < FRESH_TTL;
    if (fresh) return entry.data;

    // Stale: revalidate in background but return cached
    if (!entry.inflight) {
      entry.inflight = axios
        .get(url, options)
        .then((r) => {
          cache.set(url, { data: r.data, ts: Date.now() });
          return r.data;
        })
        .catch(() => entry.data)
        .finally(() => {
          const e = cache.get(url);
          if (e) e.inflight = null;
        });
    }
    return entry.data;
  }

  // Miss: dedupe in-flight requests
  let inflight = cache.get(`__inflight__${url}`);
  if (!inflight) {
    inflight = axios.get(url, options).then((r) => {
      cache.set(url, { data: r.data, ts: Date.now() });
      cache.delete(`__inflight__${url}`);
      return r.data;
    });
    cache.set(`__inflight__${url}`, inflight);
  }
  return inflight;
}

/** Iterate cached entries — returns array of [url, data] pairs. */
export function listCached() {
  const out = [];
  for (const [key, entry] of cache.entries()) {
    if (key.startsWith('__inflight__')) continue;
    out.push([key, entry?.data]);
  }
  return out;
}

export { API, BACKEND_URL };
